import React, { useState, useEffect, useRef, useCallback } from "react";
import L from "leaflet";
import { FaCircleInfo } from "react-icons/fa6";

const Information = () => {
  const [open, setOpen] = useState(false);
  const infoRef = useRef(null);
  // Leaflet's own drag/click handling swallows clicks on overlay controls
  // unless propagation to the map is explicitly disabled on the DOM node.
  const infoPanelRef = useCallback((node) => {
    infoRef.current = node;
    if (node) {
      L.DomEvent.disableClickPropagation(node);
      L.DomEvent.disableScrollPropagation(node);
    }
  }, []);
  const btnRef = useCallback((node) => {
    if (node) {
      L.DomEvent.disableClickPropagation(node);
      L.DomEvent.disableScrollPropagation(node);
    }
  }, []);

  // Close on outside click
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (infoRef.current && !infoRef.current.contains(e.target)) {
        setOpen(false);
      }
    };
    if (open) document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [open]);

  // Content for the Info component, combining information from both images
  const infoContent = (
    <div style={{ lineHeight: '1.4' }}>
      <h3 style={{ fontSize: '1.1em', marginTop: '18px', marginBottom: '8px', fontWeight: 'bold' }}>Climate Change Projected Hazard Maps</h3>
      <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
        <div>
          <h4 style={{ fontSize: '1em', margin: '0 0 5px 0', fontWeight: 'bold' }}>Scenario SSP 1-2.6</h4>
          <ul style={{ listStyle: 'disc', marginLeft: '15px', paddingLeft: '0' }}>
            <li>Year 2020 - 2039</li>
            <li>Year 2040 - 2059</li>
            <li>Year 2060 - 2079</li>
          </ul>
        </div>
        <div>
          <h4 style={{ fontSize: '1em', margin: '0 0 5px 0', fontWeight: 'bold' }}>Scenario SSP 3-7.0</h4>
          <ul style={{ listStyle: 'disc', marginLeft: '15px', paddingLeft: '0' }}>
            <li>Year 2020 - 2039</li>
            <li>Year 2040 - 2059</li>
            <li>Year 2060 - 2079</li>
          </ul>
        </div>
        <div>
          <h4 style={{ fontSize: '1em', margin: '0 0 5px 0', fontWeight: 'bold' }}>Scenario SSP 5-8.5</h4>
          <ul style={{ listStyle: 'disc', marginLeft: '15px', paddingLeft: '0' }}>
            <li>Year 2020 - 2039</li>
            <li>Year 2040 - 2059</li>
            <li>Year 2060 - 2079</li>
          </ul>
        </div>
      </div>

      {/* Separator or clear break if desired */}
      <hr style={{ margin: '20px 0', borderColor: '#eee' }} />

      {/* Information from the second image */}
      <h3 style={{ fontSize: '1.1em', marginBottom: '8px', fontWeight: 'bold' }}>Climate projected map information:</h3>
      <ul style={{ listStyle: 'disc', marginLeft: '15px', paddingLeft: '0' }}>
        <li>
          <strong>Scenario SSP (Shared Socioeconomic Pathways) x-x.x</strong>
          <ul style={{ listStyle: 'circle', marginLeft: '20px', paddingLeft: '0' }}>
            <li style={{ marginTop: '5px' }}>
              <strong>SSP 1 – 2.6</strong>
              <ul style={{ listStyle: 'square', marginLeft: '20px', paddingLeft: '0' }}>
                <li>Global warming stays 1.5°C above current conditions and implies a net zero CO2 emission, with very proactive global policy intervention.</li>
                <li>“best case” scenario</li>
              </ul>
            </li>
            <li style={{ marginTop: '10px' }}>
              <strong>SSP 3 – 7.0</strong>
              <ul style={{ listStyle: 'square', marginLeft: '20px', paddingLeft: '0' }}>
                <li>Global warming reaches 4°C. High greenhouse gas (GHG) emissions and very few global policy interventions.</li>
                <li>“worse case” scenario</li>
              </ul>
            </li>
            <li style={{ marginTop: '10px' }}>
              <strong>SSP 5 – 8.5</strong>
              <ul style={{ listStyle: 'square', marginLeft: '20px', paddingLeft: '0' }}>
                <li>Global warming increased to more than 4 °C. Very high GHG emissions and little to no policy intervention.</li>
                <li>“worst case” scenario</li>
              </ul>
            </li>
          </ul>
        </li>
      </ul>

      {/* Reference from the second image */}
      <p style={{ marginTop: '5px', fontSize: '0.85em' }}>
        <strong>Reference:</strong><br/>
        IPCC, 2023: Climate Change 2023: Synthesis Report. Contribution of Working Groups I, II and III to the Sixth Assessment Report of the Intergovernmental Panel on Climate Change [Core Writing Team, H. Lee and J. Romero (eds.)]. IPCC, Geneva, Switzerland, pp. 35-115, doi:{" "}
        <a href="https://doi.org/10.59327/IPCC/AR6-9789291691647" target="_blank" rel="noopener noreferrer" style={{ wordBreak: 'break-all' }}>
          10.59327/IPCC/AR6-9789291691647
        </a>
      </p>
    </div>
  );

  return (
    <div style={{ position: "absolute", bottom: "20px", left: "12px", zIndex: 999 }}>
      {/* Info Button */}
      <button
        ref={btnRef}
        onClick={() => setOpen(!open)}
        title="More Info"
        style={{
          background: "rgba(255, 255, 255, 0.15)",
          backdropFilter: "blur(10px) brightness(1.1)",
          WebkitBackdropFilter: "blur(10px) brightness(1.1)",
          border: "1px solid rgba(255, 255, 255, 0.25)",
          borderRadius: "8px",
          padding: "8px",
          boxShadow: "0 4px 12px rgba(0, 0, 0, 0.15)",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          cursor: "pointer",
          fontSize: 16,
          color: "black",
          transition: "transform 0.2s",
        }}
        aria-label="Show info"
        onMouseEnter={(e) => (e.currentTarget.style.transform = "scale(1.05)")}
        onMouseLeave={(e) => (e.currentTarget.style.transform = "scale(1.0)")}
      >
        <FaCircleInfo />
      </button>

      {/* Info Card */}
      {open && (
        <div
          ref={infoPanelRef}
          style={{
            position: "absolute",
            bottom: 30,
            left: 0,
            minWidth: 260,
            maxWidth: 350,
            background: "#fff",
            borderRadius: 12,
            boxShadow: "0 4px 24px rgba(0,0,0,0.15)",
            padding: "16px 18px",
            fontSize: 13,
            color: "#333",
            zIndex: 1000,
            animation: "fadeIn 0.3s ease",
          }}
        >
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 8 }}>
            <h5 style={{ fontWeight: 600, color: "#000" }}>Information</h5>
            <button
              onClick={() => setOpen(false)}
              style={{
                background: "none",
                border: "none",
                fontSize: 18,
                cursor: "pointer",
                color: "#888",
              }}
              aria-label="Close info"
            >
              ×
            </button>
          </div>
          <div style={{ height: '300px', overflowY: 'auto', paddingRight: '10px' }}>
            {infoContent}
          </div>
        </div>
      )}

      {/* Fade-in Animation (consider moving to a CSS file for global styles) */}
      <style>{`
        @keyframes fadeIn {
          from { opacity: 0; transform: translateY(5px); }
          to { opacity: 1; transform: translateY(0); }
        }
      `}</style>
    </div>
  );
};

export default Information;